import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { message, Modal, Tabs } from "antd";
import { useNavigate } from "react-router-dom";
import {
  ProductScreen, Appbar, Summary, SearchBar, Chips, EmptyState, Fab, Sheet,
  ActionRow, DetailRow, Field, TextInput, TextAreaInput, SelectInput, Pill,
  Mono, Note, Ico, fmt, useMobileMenu,
} from "../produits/productMobileKit";
import { useSelection } from "../../SelectionContext";
import { useAuth } from "../../context/AuthContext";
import ExpenseExportModal from "../../gestion-depenses/components/ExpenseExportModal";
import ExpenseDocumentsPanel from "../../gestion-depenses/components/ExpenseDocumentsPanel";
import { getExpenseDocuments } from "../../gestion-depenses/expenseWorkspaceUtils";
import { useExpenseWorkspaceStyle } from "../../gestion-depenses/ExpenseWorkspace";
import useExpenseFormOptions from "../../gestion-depenses/useExpenseFormOptions";
import {
  getSuggestedTreasuryAccountId,
  useCommercialPaymentRoutes,
} from "../../hooks/useCommercialTreasurySuggestion";
import {
  deleteExpense,
  fetchExpense,
  fetchExpenseDocumentOptions,
  fetchExpensePaymentModes,
  fetchExpensePayments,
  fetchExpenseTreasuryAccounts,
  fetchExpenses,
  processExpensePayment,
  saveExpense,
  uploadExpenseDocument,
} from "./expensesMobileApi";

const PAGE = 20;
const DEFAULT_PERMISSIONS = {
  VIEW: "Gestion Commerciale.Depenses.SaisieDepenses.view",
  CREATE: "Gestion Commerciale.Depenses.SaisieDepenses.create",
  EDIT: "Gestion Commerciale.Depenses.SaisieDepenses.edit",
  DELETE: "Gestion Commerciale.Depenses.SaisieDepenses.delete",
  EXPORT: "Gestion Commerciale.Depenses.SaisieDepenses.export",
  CREATE_PAYMENT:
    "Gestion Commerciale.Depenses.SaisieDepenses.create_payment",
};

const today = () => new Date().toISOString().slice(0, 10);
const amount = (value) => `${new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(Number(value) || 0)} F`;
const numberValue = (value) => {
  const parsed = Number(String(value ?? "").replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(parsed) ? parsed : 0;
};
const dateLabel = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString("fr-FR") : "—";
};
const dateOnlyLabel = (value) => {
  const [year, month, day] = String(value || "").slice(0, 10).split("-");
  return year && month && day ? `${day}/${month}/${year}` : "—";
};
const errorMessage = (error, fallback) =>
  error?.response?.data?.error || error?.response?.data?.message || error?.message || fallback;
const idempotencyKey = () => `${Date.now()}-${Math.floor(Math.random() * 1000000)}`;
const emptyForm = () => ({
  expense_category_id: "", supplier_id: "", amount: "", date: today(), due_date: today(), bill: "", notes: "",
  document_url: null, document_name: null, documents: [],
});
const emptyPayment = () => ({
  treasury_account_id: "", payment_mode_id: "", amount: "", date: today(), notes: "",
});

const statusMeta = (row) => {
  const total = Number(row.amount || 0);
  const paid = Number(row.paid_amount || 0);
  if (row.payment_status === "Payé" || (total > 0 && paid >= total)) return { tone: "ok", label: "Payée" };
  if (paid > 0 || row.payment_status === "Partiellement payé") return { tone: "warn", label: "Partielle" };
  return { tone: "danger", label: "Non payée" };
};

export default function ExpensesMobile({
  moduleScope = "commercial",
  permissions = DEFAULT_PERMISSIONS,
  title = "Saisie des dépenses",
  onNavigateSibling,
}) {
  const onMenu = useMobileMenu();
  const navigate = useNavigate();
  const { selectedCompany, selectedWarehouse } = useSelection();
  const { user, hasPermission, isLoading: authLoading } = useAuth();
  const documentStyle = useExpenseWorkspaceStyle();
  const scopeKey = `${selectedCompany}:${selectedWarehouse}:${moduleScope}`;
  const scopeRef = useRef(scopeKey);
  scopeRef.current = scopeKey;

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [filters, setFilters] = useState({ supplier_id: "", start_date: "", end_date: "" });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const { categories, suppliers, loading: dependenciesLoading, error: dependencyError,
    refresh: loadDependencies, onChoiceOpen } = useExpenseFormOptions({
    userId: user?.id, companyId: selectedCompany, warehouseId: selectedWarehouse, moduleScope,
    enabled: !authLoading && [permissions.VIEW, permissions.CREATE, permissions.EDIT].some((key) => hasPermission(key)),
    formOpen,
  });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [maxDocuments, setMaxDocuments] = useState(1);
  const [documentOptionsLoading, setDocumentOptionsLoading] = useState(false);
  const [documentOptionsError, setDocumentOptionsError] = useState('');
  const [documentOptionsRetry, setDocumentOptionsRetry] = useState(0);
  const formOpenRef = useRef(formOpen);
  formOpenRef.current = formOpen;
  const documentSessionRef = useRef(0);
  useEffect(() => {
    documentSessionRef.current += 1;
    setUploading(false);
  }, [formOpen, scopeKey]);
  useEffect(() => {
    if (!formOpen) return undefined;
    const controller = new AbortController();
    setDocumentOptionsLoading(true); setDocumentOptionsError('');
    fetchExpenseDocumentOptions(selectedCompany, selectedWarehouse, moduleScope, controller.signal).then((options) => {
      if (!controller.signal.aborted) setMaxDocuments(options?.maxDocuments === 10 ? 10 : 1);
    }).catch(() => { if (!controller.signal.aborted) setDocumentOptionsError('Impossible de charger les options des justificatifs.'); })
      .finally(() => { if (!controller.signal.aborted) setDocumentOptionsLoading(false); });
    return () => controller.abort();
  }, [formOpen, selectedCompany, selectedWarehouse, moduleScope, documentOptionsRetry]);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentWarehouseId, setPaymentWarehouseId] = useState(null);
  const [paymentForm, setPaymentForm] = useState(emptyPayment);
  const [paymentModes, setPaymentModes] = useState([]);
  const [treasuryAccounts, setTreasuryAccounts] = useState([]);
  const { routes: paymentRoutes, loaded: routesLoaded } =
    useCommercialPaymentRoutes(paymentWarehouseId, paymentOpen && moduleScope === "commercial");

  useEffect(() => {
    if (moduleScope !== "commercial" || !paymentOpen || !routesLoaded ||
        !paymentForm.payment_mode_id || paymentForm.treasury_account_id) return;
    const accountId = getSuggestedTreasuryAccountId(
      paymentForm.payment_mode_id, paymentRoutes, treasuryAccounts,
    );
    if (accountId) {
      setPaymentForm((current) => !current.treasury_account_id
        ? { ...current, treasury_account_id: String(accountId) }
        : current);
    }
  }, [moduleScope, paymentOpen, routesLoaded, paymentForm.payment_mode_id, paymentForm.treasury_account_id, paymentRoutes, treasuryAccounts]);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const inFlight = useRef(false);
  const handledDeepLink = useRef(null);

  const canCreate = hasPermission(permissions.CREATE);
  const canEdit = hasPermission(permissions.EDIT);
  const canDelete = hasPermission(permissions.DELETE);
  const canExport = hasPermission(permissions.EXPORT);
  const canCreatePayment = hasPermission(permissions.CREATE_PAYMENT);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query), 400);
    return () => window.clearTimeout(timer);
  }, [query]);

  const load = useCallback(async (nextPage = 1, append = false) => {
    if (!selectedCompany || !selectedWarehouse || !hasPermission(permissions.VIEW)) return;
    setLoading(true);
    try {
      const params = {
        companyId: selectedCompany,
        warehouseId: selectedWarehouse,
        page: nextPage,
        limit: PAGE,
        search: debouncedQuery,
        module_scope: moduleScope,
      };
      if (filters.supplier_id) params.supplierId = filters.supplier_id;
      if (filters.start_date) params.startDate = new Date(`${filters.start_date}T00:00:00`).toISOString();
      if (filters.end_date) params.endDate = new Date(`${filters.end_date}T23:59:59`).toISOString();
      const result = await fetchExpenses(params);
      setRows((prev) => append ? [...prev, ...result.rows] : result.rows);
      setTotal(result.total);
      setPage(nextPage);
    } catch (error) {
      message.error(errorMessage(error, "Impossible de charger les dépenses"));
      if (!append) setRows([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedQuery, filters, hasPermission, moduleScope, permissions.VIEW, selectedCompany, selectedWarehouse]);

  useEffect(() => {
    setFormOpen(false); setEditing(null); setForm(emptyForm());
    setFiltersOpen(false);
    setFilters((current) => current.supplier_id ? { ...current, supplier_id: "" } : current);
  }, [scopeKey]);
  useEffect(() => {
    if (!authLoading) load(1, false);
  }, [authLoading, load]);

  const visible = useMemo(() => rows.filter((row) => {
    if (statusFilter === "all") return true;
    const meta = statusMeta(row);
    return (statusFilter === "paid" && meta.tone === "ok") ||
      (statusFilter === "partial" && meta.tone === "warn") ||
      (statusFilter === "unpaid" && meta.tone === "danger");
  }), [rows, statusFilter]);

  const pageStats = useMemo(() => rows.reduce((acc, row) => {
    acc.total += Number(row.amount || 0);
    acc.paid += Number(row.paid_amount || 0);
    return acc;
  }, { total: 0, paid: 0 }), [rows]);

  const openForm = (row = null) => {
    const hasPayments = Number(row?.paid_amount || 0) > 0;
    if (row && hasPayments) {
      message.warning("Une dépense déjà payée ne peut plus être modifiée");
      return;
    }
    setEditing(row);
    setForm(row ? {
      expense_category_id: String(row.expense_category_id || ""),
      supplier_id: String(row.supplier_id || ""),
      amount: String(row.amount ?? ""),
      date: row.date ? String(row.date).slice(0, 10) : today(),
      due_date: row.due_date ? String(row.due_date).slice(0, 10) : "",
      bill: row.bill || "",
      notes: row.notes || "",
      document_url: row.document_url || null,
      document_name: row.document_name || null,
      documents: getExpenseDocuments(row),
    } : emptyForm());
    setFormOpen(true);
    setDetail(null);
  };

  const handleFile = async (file) => {
    if (!file || uploading || documentOptionsLoading || documentOptionsError) return;
    if (form.documents.length >= maxDocuments) { message.warning(`La charge accepte ${maxDocuments} justificatif(s) maximum.`); return; }
    const requestScope = scopeKey;
    const requestSession = documentSessionRef.current;
    setUploading(true);
    try {
      const result = await uploadExpenseDocument(file, moduleScope, selectedCompany, selectedWarehouse);
      if (scopeRef.current !== requestScope || !formOpenRef.current || documentSessionRef.current !== requestSession) return;
      if (!result?.success) throw new Error("Téléchargement impossible");
      setForm((prev) => ({ ...prev, documents: [...prev.documents, { url: result.url, name: result.originalName }] }));
      message.success("Document joint");
    } catch (error) {
      if (scopeRef.current === requestScope && formOpenRef.current && documentSessionRef.current === requestSession) message.error(errorMessage(error, "Impossible de joindre le document"));
    } finally {
      if (scopeRef.current === requestScope && documentSessionRef.current === requestSession) setUploading(false);
    }
  };

  const persistExpense = async (numericAmount) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setSaving(true);
    try {
      await saveExpense(editing?.id, {
        expense_category_id: form.expense_category_id,
        supplier_id: form.supplier_id || null,
        amount: numericAmount,
        date: form.date ? new Date(`${form.date}T00:00:00`).toISOString() : null,
        due_date: form.due_date || null,
        bill: form.bill.trim(),
        notes: form.notes.trim(),
        document_url: form.documents[0]?.url || null,
        document_name: form.documents[0]?.name || null,
        documents: form.documents.map((document) => ({ url: document.url, name: document.name })),
        company_id: selectedCompany,
        warehouse_id: selectedWarehouse,
        user_id: user.id,
        module_scope: moduleScope,
      });
      message.success(editing ? "Dépense mise à jour" : "Dépense ajoutée");
      setFormOpen(false);
      setEditing(null);
      await load(1, false);
    } catch (error) {
      message.error(errorMessage(error, "Impossible d'enregistrer la dépense"));
    } finally {
      setSaving(false);
      inFlight.current = false;
    }
  };

  const submit = async () => {
    if (uploading || documentOptionsLoading || documentOptionsError) return;
    const numericAmount = numberValue(form.amount);
    if (!form.expense_category_id || !form.due_date || numericAmount <= 0 || !selectedWarehouse || !user?.id) return;

    if (!form.documents.length) {
      Modal.confirm({
        title: "Enregistrer sans pièce justificative ?",
        content:
          "La dépense restera autorisée, mais elle sera plus difficile à contrôler, à justifier et à valider en comptabilité. Ajoutez de préférence une facture, un reçu ou une autre preuve de la dépense.",
        okText: "Enregistrer quand même",
        cancelText: "Ajouter un justificatif",
        okButtonProps: { danger: true },
        centered: true,
        onOk: () => persistExpense(numericAmount),
      });
      return;
    }

    await persistExpense(numericAmount);
  };

  const openDetail = async (row) => {
    setDetail(row);
    setPayments([]);
    setPaymentsLoading(true);
    try {
      const [fresh, paymentRows] = await Promise.all([
        fetchExpense(row.id, moduleScope),
        fetchExpensePayments(row.id, selectedCompany, moduleScope),
      ]);
      setDetail(fresh || row);
      setPayments(paymentRows);
    } catch (error) {
      message.error(errorMessage(error, "Impossible de charger le détail"));
    } finally {
      setPaymentsLoading(false);
    }
  };

  const remove = (row) => {
    if (Number(row.paid_amount || 0) > 0) return;
    Modal.confirm({
      title: "Supprimer cette dépense ?",
      content: `${row.category_name || "Dépense"} · ${amount(row.amount)}`,
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      centered: true,
      onOk: async () => {
        try {
          await deleteExpense(row.id, moduleScope);
          message.success("Dépense supprimée");
          setDetail(null);
          load(1, false);
        } catch (error) {
          message.error(errorMessage(error, "Suppression impossible"));
        }
      },
    });
  };

  const openPayment = async (expenseOverride = null) => {
    const targetExpense = expenseOverride?.supplier_id
      ? expenseOverride
      : detail;
    if (!canCreatePayment) {
      message.warning(
        "Vous n'avez pas la permission d'enregistrer un paiement sur cette dépense"
      );
      return;
    }
    if (!targetExpense?.supplier_id) {
      message.warning("Ajoutez un fournisseur à cette dépense avant d'enregistrer un paiement");
      return;
    }
    setPaymentLoading(true);
    try {
      const [modes, accounts] = await Promise.all([
        fetchExpensePaymentModes(selectedCompany),
        fetchExpenseTreasuryAccounts(selectedCompany, targetExpense.warehouse_id || selectedWarehouse, moduleScope),
      ]);
      setPaymentModes(modes);
      setTreasuryAccounts(accounts);
      setPaymentWarehouseId(targetExpense.warehouse_id || selectedWarehouse);
      const remaining = Math.max(Number(targetExpense.due_amount || 0) || Number(targetExpense.amount || 0) - Number(targetExpense.paid_amount || 0), 0);
      const defaultAccount = moduleScope === "commercial"
        ? null
        : accounts.find((account) => Number(account.is_default) === 1) || accounts[0];
      setPaymentForm({ ...emptyPayment(), amount: String(remaining), treasury_account_id: String(defaultAccount?.id || "") });
      setPaymentOpen(true);
    } catch (error) {
      message.error(errorMessage(error, "Impossible de préparer le paiement"));
    } finally {
      setPaymentLoading(false);
    }
  };

  useEffect(() => {
    if (
      authLoading ||
      !selectedCompany ||
      !selectedWarehouse ||
      !hasPermission(permissions.VIEW)
    ) return;

    const params = new URLSearchParams(window.location.search);
    const expenseId = Number(params.get("expense_id"));
    if (!Number.isInteger(expenseId) || expenseId <= 0) return;
    const key = `${moduleScope}:${expenseId}:${params.get("action") || "view"}`;
    if (handledDeepLink.current === key) return;
    handledDeepLink.current = key;

    const openLinkedExpense = async () => {
      try {
        const [expense, paymentRows] = await Promise.all([
          fetchExpense(expenseId, moduleScope),
          fetchExpensePayments(expenseId, selectedCompany, moduleScope),
        ]);
        if (
          !expense ||
          Number(expense.company_id) !== Number(selectedCompany) ||
          Number(expense.warehouse_id) !== Number(selectedWarehouse)
        ) {
          throw new Error(
            "Cette dépense ne correspond pas à l'établissement sélectionné."
          );
        }
        setDetail(expense);
        setPayments(paymentRows);
        if (params.get("action") === "pay" && canCreatePayment) {
          await openPayment(expense);
        }
      } catch (error) {
        handledDeepLink.current = null;
        message.error(errorMessage(error, "Impossible d'ouvrir cette dépense"));
      }
    };

    openLinkedExpense();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    authLoading,
    selectedCompany,
    selectedWarehouse,
    hasPermission,
    permissions.VIEW,
    moduleScope,
    canCreatePayment,
  ]);

  const submitPayment = async () => {
    if (!canCreatePayment) {
      message.error(
        "Vous n'avez pas la permission d'enregistrer ce paiement"
      );
      return;
    }
    const numericAmount = numberValue(paymentForm.amount);
    const remaining = Math.max(Number(detail?.due_amount || 0) || Number(detail?.amount || 0) - Number(detail?.paid_amount || 0), 0);
    if (inFlight.current || !detail || !paymentForm.treasury_account_id || !paymentForm.payment_mode_id || numericAmount <= 0 || numericAmount > remaining) return;
    inFlight.current = true;
    setPaymentLoading(true);
    try {
      await processExpensePayment({
        expense_id: detail.id,
        company_id: selectedCompany,
        warehouse_id: detail.warehouse_id || selectedWarehouse,
        payment_mode_id: paymentForm.payment_mode_id,
        treasury_account_id: paymentForm.treasury_account_id,
        amount: numericAmount,
        date: paymentForm.date ? new Date(`${paymentForm.date}T00:00:00`).toISOString() : null,
        notes: paymentForm.notes.trim(),
        module_scope: moduleScope,
        staff_user_id: user?.id || null,
      }, idempotencyKey());
      message.success("Paiement enregistré");
      setPaymentOpen(false);
      await openDetail(detail);
      await load(1, false);
    } catch (error) {
      message.error(errorMessage(error, "Paiement impossible"));
    } finally {
      setPaymentLoading(false);
      inFlight.current = false;
    }
  };

  if (authLoading) return <ProductScreen><Appbar title={title} onMenu={onMenu} /><div className="body"><EmptyState loading title="Chargement…" /></div></ProductScreen>;
  if (!hasPermission(permissions.VIEW)) return <ProductScreen><Appbar title={title} onMenu={onMenu} /><div className="body"><EmptyState icon="alert-circle" title="Accès non autorisé" text="Vous n'avez pas la permission d'afficher les dépenses." /></div></ProductScreen>;
  if (!selectedCompany || !selectedWarehouse) return <ProductScreen><Appbar title={title} onMenu={onMenu} /><div className="body"><EmptyState icon="store" title="Sélection requise" text="Choisissez une entreprise et un établissement." /></div></ProductScreen>;

  const remainingPage = Math.max(pageStats.total - pageStats.paid, 0);
  const supplierOptions = suppliers.map((row) => ({ value: String(row.id), label: row.name }));
  const categoryOptions = categories.map((row) => ({ value: String(row.id), label: row.name }));
  const detailRemaining = detail ? Math.max(Number(detail.due_amount || 0) || Number(detail.amount || 0) - Number(detail.paid_amount || 0), 0) : 0;
  const detailStatus = detail ? statusMeta(detail) : null;
  const canPayDetail =
    detail && detailRemaining > 0 && detail.supplier_id && canCreatePayment;

  return (
    <ProductScreen>
      <Appbar
        title={title}
        subtitle={`${fmt(total)} dépense${total > 1 ? "s" : ""}`}
        onMenu={onMenu}
        action={<button className="iconbtn" aria-label="Changer de rubrique" onClick={() => setNavOpen(true)}><Ico name="list-ordered" /></button>}
      />
      <div className="appbar" style={{ borderTop: 0, paddingTop: 0 }}>
        <Summary items={[{ label: "Total page", value: amount(pageStats.total) }, { label: "Payé", value: amount(pageStats.paid) }, { label: "Restant", value: amount(remainingPage) }]} />
        <SearchBar value={query} onChange={setQuery} placeholder="Facture, notes, catégorie…" />
        <Chips value={statusFilter} onChange={setStatusFilter} options={[
          { key: "all", label: "Toutes" }, { key: "unpaid", label: "Non payées" }, { key: "partial", label: "Partielles" }, { key: "paid", label: "Payées" },
        ]} />
        <button className="btn btn--ghost" onClick={() => setFiltersOpen(true)}><Ico name="sliders-horizontal" />Filtres avancés</button>
      </div>
      <div className="body">
        {loading && !rows.length ? <EmptyState loading title="Chargement…" /> : !visible.length ? (
          <EmptyState icon="banknote" title="Aucune dépense" text={query || statusFilter !== "all" ? "Aucun résultat pour ces filtres." : "Ajoutez votre première dépense."} />
        ) : (
          <div className="list">
            {visible.map((row) => {
              const meta = statusMeta(row);
              return (
                <button key={row.id} className="rcard" onClick={() => openDetail(row)}>
                  <Mono name={row.category_name || "D"} square />
                  <div className="rcard__main">
                    <div className="rcard__name">{row.category_name || "Dépense"}</div>
                    <div className="rcard__meta"><Pill tone={meta.tone} label={meta.label} /><span className="rcard__sub">{row.supplier_name || "Sans fournisseur"} · {dateLabel(row.date)}</span></div>
                    {row.bill ? <div className="rcard__sub">Facture {row.bill}</div> : null}
                  </div>
                  <div className="rcard__price">{amount(row.amount)}</div>
                  <span className="rcard__chev"><Ico name="chevron-right" /></span>
                </button>
              );
            })}
            {rows.length < total ? <button className="btn btn--ghost" disabled={loading} onClick={() => load(page + 1, true)}>{loading ? "Chargement…" : `Charger plus (${rows.length}/${total})`}</button> : null}
          </div>
        )}
      </div>
      {canCreate ? <Fab label="Nouvelle dépense" onClick={() => openForm()} /> : null}

      <Sheet open={navOpen} onClose={() => setNavOpen(false)} title="Aller à…">
        <ActionRow icon="chevron-right" label="Catégories de dépenses" onClick={() => onNavigateSibling ? onNavigateSibling("categories") : navigate("/gestion-charges-admin/categories")} />
        {canExport ? <ActionRow icon="file-text" label="Exporter les dépenses" onClick={() => { setNavOpen(false); setExportOpen(true); }} /> : null}
      </Sheet>

      <Sheet open={filtersOpen} onClose={() => setFiltersOpen(false)} title="Filtres avancés">
        {dependencyError && <Note>{dependencyError}<button className="btn btn--ghost" disabled={dependenciesLoading}
          onClick={loadDependencies}>Réessayer les listes</button></Note>}
        <Field label="Fournisseur"><SelectInput value={filters.supplier_id} onChange={(supplier_id) => setFilters((f) => ({ ...f, supplier_id }))} options={[{ value: "", label: "Tous" }, ...supplierOptions]} /></Field>
        <Field label="Date début"><div className="ctrl"><input type="date" value={filters.start_date} onChange={(e) => setFilters((f) => ({ ...f, start_date: e.target.value }))} /></div></Field>
        <Field label="Date fin"><div className="ctrl"><input type="date" value={filters.end_date} onChange={(e) => setFilters((f) => ({ ...f, end_date: e.target.value }))} /></div></Field>
        <div className="sheet__actions"><button className="btn btn--ghost" onClick={() => setFilters({ supplier_id: "", start_date: "", end_date: "" })}>Réinitialiser</button><button className="btn btn--primary" onClick={() => setFiltersOpen(false)}>Appliquer</button></div>
      </Sheet>

      <div className="expense-mobile-modals" style={documentStyle}>
      <Sheet open={Boolean(detail)} onClose={() => setDetail(null)} title={detail?.category_name || "Détail de la dépense"} subtitle={detail ? amount(detail.amount) : undefined}>
        {detail ? <>
          <Tabs destroyInactiveTabPane animated={{ tabPane: false }} items={[
            { key: 'info', label: 'Informations générales', children: <>
          <DetailRow label="Statut" value={detailStatus.label} />
          <DetailRow label="Date" value={dateLabel(detail.date)} />
          <DetailRow label="Échéance de paiement" value={detail.due_date ? dateOnlyLabel(detail.due_date) : "Non renseignée"} />
          <DetailRow label="Établissement" value={detail.warehouse_name || "—"} />
          <DetailRow label="Fournisseur" value={detail.supplier_name || "—"} />
          <DetailRow label="Référence facture" value={detail.bill || "—"} />
          <DetailRow label="Montant" value={amount(detail.amount)} />
          <DetailRow label="Montant payé" value={amount(detail.paid_amount)} />
          <DetailRow label="Montant restant" value={amount(detailRemaining)} />
          <DetailRow label="Notes" value={detail.notes || "—"} />
          <DetailRow label="Enregistré par" value={detail.user_name || "—"} />
            </> },
            { key: 'payments', label: 'Paiements', children: <>
          {paymentsLoading ? <Note>Chargement des paiements…</Note> : !payments.length ? <Note>Aucun paiement enregistré.</Note> : payments.map((payment) => (
            <div className="preview" key={payment.payment_id || payment.expense_payment_id || payment.id} style={{ marginBottom: 8 }}>
              <DetailRow label={payment.payment_mode_name || "Paiement"} value={amount(payment.payment_amount ?? payment.amount)} />
              <DetailRow label="Date" value={dateLabel(payment.payment_date || payment.date)} />
            </div>
          ))}
            </> },
            { key: 'documents', label: 'Document', children: <div>
              <ExpenseDocumentsPanel expense={detail} documents={getExpenseDocuments(detail)} companyId={selectedCompany} moduleScope={moduleScope} />
            </div> },
          ]} />
          {canPayDetail ? <ActionRow icon="banknote" label="Ajouter un paiement" onClick={openPayment} /> : null}
          {canEdit && Number(detail.paid_amount || 0) <= 0 ? <ActionRow icon="pencil" label="Modifier" onClick={() => openForm(detail)} /> : null}
          {canDelete && Number(detail.paid_amount || 0) <= 0 ? <ActionRow icon="trash-2" label="Supprimer" danger onClick={() => remove(detail)} /> : null}
        </> : null}
      </Sheet>

      <Sheet open={formOpen} onClose={() => setFormOpen(false)} title={editing ? "Modifier la dépense" : "Nouvelle dépense"}>
        {dependencyError && <Note>{dependencyError}<button className="btn btn--ghost" disabled={dependenciesLoading}
          onClick={loadDependencies}>Réessayer les listes</button></Note>}
        <Field label="Date *"><div className="ctrl"><input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} /></div></Field>
        <Field label="Date d’échéance du paiement *" hint="Date à laquelle le solde doit être réglé"><div className="ctrl"><input type="date" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} /></div></Field>
        <div onFocus={() => onChoiceOpen(true)}>
          <Field label="Catégorie *"><SelectInput value={form.expense_category_id} onChange={(expense_category_id) => setForm((f) => ({ ...f, expense_category_id }))} options={categoryOptions} placeholder="Sélectionner" /></Field>
        </div>
        <Field label="Montant *"><TextInput value={form.amount} onChange={(value) => setForm((f) => ({ ...f, amount: value }))} inputMode="decimal" placeholder="0" /></Field>
        <div onFocus={() => onChoiceOpen(true)}>
          <Field label="Fournisseur (recommandé)" hint="Nécessaire pour ajouter un paiement"><SelectInput value={form.supplier_id} onChange={(supplier_id) => setForm((f) => ({ ...f, supplier_id }))} options={[{ value: "", label: "Non renseigné — paiement impossible" }, ...supplierOptions]} /></Field>
        </div>
        <Field label="Référence facture"><TextInput value={form.bill} onChange={(bill) => setForm((f) => ({ ...f, bill }))} /></Field>
        <Field label="Notes"><TextAreaInput value={form.notes} onChange={(notes) => setForm((f) => ({ ...f, notes }))} rows={3} /></Field>
        <Note>Justificatifs recommandés. Enregistrement possible sans document.</Note>
        {documentOptionsError && <Note>{documentOptionsError}<button className="btn btn--ghost" onClick={() => setDocumentOptionsRetry((value) => value + 1)}>Réessayer</button></Note>}
        <Field label="Pièces justificatives (fortement recommandées)" hint={`PDF, JPEG ou PNG · 10 Mo par fichier · ${maxDocuments} document${maxDocuments > 1 ? 's' : ''} maximum`}>
          <div className="ctrl"><input type="file" accept=".pdf,.jpg,.jpeg,.png" disabled={uploading || documentOptionsLoading || !!documentOptionsError || form.documents.length >= maxDocuments} onChange={(e) => handleFile(e.target.files?.[0])} /></div>
          {form.documents.map((document, index) => <div key={document.url} className="preview">
            <span>{document.name}</span><button className="btn btn--ghost" disabled={uploading || saving}
              onClick={() => setForm((previous) => ({ ...previous, documents: previous.documents.filter((_, position) => position !== index) }))}>Retirer</button>
          </div>)}
        </Field>
        {dependenciesLoading ? <Note>Chargement des listes…</Note> : null}
        <div className="sheet__actions"><button className="btn btn--ghost" onClick={() => setFormOpen(false)}>Annuler</button><button className="btn btn--primary" onClick={submit} disabled={saving || uploading || documentOptionsLoading || !!documentOptionsError || !form.expense_category_id || !form.due_date || numberValue(form.amount) <= 0}>{saving ? "Enregistrement…" : editing ? "Mettre à jour" : "Créer"}</button></div>
      </Sheet>
      </div>

      <Sheet open={paymentOpen} onClose={() => setPaymentOpen(false)} title="Ajouter un paiement" subtitle={detail ? `Restant : ${amount(detailRemaining)}` : undefined}>
        {!treasuryAccounts.length ? <Note>Aucun compte de trésorerie actif. Créez-en un dans Caisse & banques.</Note> : null}
        <Field label="Date *"><div className="ctrl"><input type="date" value={paymentForm.date} onChange={(e) => setPaymentForm((f) => ({ ...f, date: e.target.value }))} /></div></Field>
        <Field label="Montant *"><TextInput value={paymentForm.amount} onChange={(value) => setPaymentForm((f) => ({ ...f, amount: value }))} inputMode="decimal" /></Field>
        <Field label="Compte de trésorerie *"><SelectInput value={paymentForm.treasury_account_id} onChange={(treasury_account_id) => setPaymentForm((f) => ({ ...f, treasury_account_id }))} options={treasuryAccounts.map((row) => ({ value: String(row.id), label: row.name }))} placeholder="Sélectionner" /></Field>
        <Field label="Mode de paiement *"><SelectInput value={paymentForm.payment_mode_id} onChange={(payment_mode_id) => {
          const accountId = moduleScope === "commercial" && routesLoaded
            ? getSuggestedTreasuryAccountId(payment_mode_id, paymentRoutes, treasuryAccounts)
            : undefined;
          setPaymentForm((f) => ({
            ...f,
            payment_mode_id,
            treasury_account_id: moduleScope === "commercial"
              ? accountId ? String(accountId) : ""
              : f.treasury_account_id,
          }));
        }} options={paymentModes.map((row) => ({ value: String(row.id), label: `${row.name}${row.company_id ? " · Spécifique" : ""}` }))} placeholder="Sélectionner" /></Field>
        <Field label="Notes"><TextAreaInput value={paymentForm.notes} onChange={(notes) => setPaymentForm((f) => ({ ...f, notes }))} rows={2} /></Field>
        <div className="sheet__actions"><button className="btn btn--ghost" onClick={() => setPaymentOpen(false)}>Annuler</button><button className="btn btn--primary" onClick={submitPayment} disabled={paymentLoading || !paymentForm.treasury_account_id || !paymentForm.payment_mode_id || numberValue(paymentForm.amount) <= 0 || numberValue(paymentForm.amount) > detailRemaining}>{paymentLoading ? "Enregistrement…" : "Enregistrer"}</button></div>
      </Sheet>

      <ExpenseExportModal
        visible={exportOpen}
        onClose={() => setExportOpen(false)}
        moduleScope={moduleScope}
        existingFilters={{
          warehouseId: selectedWarehouse,
          search: debouncedQuery,
          supplierIds: filters.supplier_id ? [filters.supplier_id] : [],
          dateRange: null,
        }}
      />
    </ProductScreen>
  );
}
